import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { 
  Calendar as CalendarIcon, 
  User, 
  CheckCircle, 
  Clock, 
  MessageSquare, 
  Search,
  ChevronDown,
  ChevronRight,
  MapPin,
  AlertCircle
} from 'lucide-react';
import './ConfirmedAppointments.css';

const ConfirmedAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedAppId, setExpandedAppId] = useState(null);

  const fetchConfirmed = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .or('verification_status.eq.confirmed,status.eq.confirmado')
        .order('appointment_date', { ascending: false })
        .order('appointment_time', { ascending: true });
        
      if (error) throw error;
      setAppointments(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfirmed();
  }, []);

  const toggleExpand = (id) => {
    setExpandedAppId(expandedAppId === id ? null : id);
  };

  const filtered = appointments.filter(app => {
    const term = searchTerm.toLowerCase();
    const dniStr = String(app.study_details?.patient_dni || app.patient_dni || '');
    const patientNameStr = String(app.patient_name || '').toLowerCase();
    return patientNameStr.includes(term) || dniStr.includes(searchTerm);
  });

  return (
    <div className="confirmed-page">
      <div className="confirmed-main card glass-panel-premium" style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', position: 'relative', padding: 0 }}>
        <div className="confirmed-filters">
          <div className="search-bar-confirmed">
            <Search size={18} />
            <input 
              type="text" 
              placeholder="Buscar por paciente o DNI..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Sticky Header Wrapper */}
        <div className="sticky-table-header-block">
          <table className="confirmed-table" style={{ margin: 0, tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th style={{ width: '25%' }}>Paciente</th>
                <th style={{ width: '20%' }}>Fecha y Hora</th>
                <th style={{ width: '20%' }}>Profesional / Estudio</th>
                <th style={{ width: '23%' }}>Mensaje del Paciente</th>
                <th style={{ width: '120px', textAlign: 'center' }}>Estado</th>
              </tr>
            </thead>
          </table>
        </div>

        <div className="confirmed-table-wrapper">
          <table className="confirmed-table" style={{ tableLayout: 'fixed' }}>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>Cargando confirmaciones...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>No hay turnos confirmados.</td></tr>
              ) : filtered.map(app => {
                const isExpanded = expandedAppId === app.id;
                
                return (
                  <React.Fragment key={app.id}>
                    <tr 
                      className={`clickable-row ${isExpanded ? 'row-expanded' : ''}`}
                      onClick={() => toggleExpand(app.id)}
                    >
                      <td style={{ width: '40px', verticalAlign: 'middle', textAlign: 'center' }}>
                        <button className="expand-toggle-btn">
                          {isExpanded ? (
                            <ChevronDown size={18} className="expand-icon active" color="#007799" />
                          ) : (
                            <ChevronRight size={18} className="expand-icon" />
                          )}
                        </button>
                      </td>
                      <td style={{ width: '25%' }}>
                        <div className="cell-flex">
                          <User size={16} color="#00e5ff" />
                          <div>
                            <strong>{app.patient_name}</strong>
                            <div style={{ fontSize: '0.8rem', color: 'gray' }}>
                              DNI: {app.study_details?.patient_dni || app.patient_dni || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ width: '20%' }}>
                        <div className="cell-flex">
                          <CalendarIcon size={16} color="#1e88e5" />
                          <div>
                            <strong>{app.study_details?.confirmed_date || app.appointment_date}</strong>
                            <div style={{ fontSize: '0.8rem', color: 'gray' }}>
                              <Clock size={12} /> {app.study_details?.confirmed_time || app.appointment_time?.substring(0,5)} hs
                              {app.study_details?.confirmed_day && ` (${app.study_details.confirmed_day})`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ width: '20%' }}>
                        <div className="cell-flex-col">
                          <strong>{app.professional_name}</strong>
                          <span style={{ fontSize: '0.8rem', color: 'gray' }}>{app.study_type || 'Consulta'}</span>
                        </div>
                      </td>
                      <td style={{ width: '23%' }}>
                        {app.patient_message || app.study_details?.confirmed_details ? (
                          <div className="patient-msg-box">
                            <MessageSquare size={14} />
                            <span>{app.study_details?.confirmed_details || app.patient_message}</span>
                          </div>
                        ) : (
                          <span style={{ color: 'gray', fontStyle: 'italic', fontSize: '0.85rem' }}>Sin mensaje</span>
                        )}
                      </td>
                      <td style={{ width: '120px', textAlign: 'center', verticalAlign: 'middle' }}>
                        <div className="status-badge-confirmed">
                          <CheckCircle size={14} /> Confirmado
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="expandable-row-details">
                        <td colSpan="6" style={{ padding: '0 1rem 1rem 1rem' }}>
                          <div className="confirmed-details-container animate-fade-in">
                            <div className="confirmed-details-header">
                              <h3>Detalles de Asistencia Confirmada</h3>
                              <span className="info-badge">
                                <CheckCircle size={12} /> Datos Verificados
                              </span>
                            </div>

                            <div className="confirmed-details-grid">
                              <div className="confirmed-detail-card">
                                <div className="detail-card-icon day">
                                  <CalendarIcon size={16} />
                                </div>
                                <div className="detail-card-content">
                                  <span className="detail-label">Día de la semana</span>
                                  <span className="detail-value">
                                    {app.study_details?.confirmed_day || 'No especificado'}
                                  </span>
                                </div>
                              </div>

                              <div className="confirmed-detail-card">
                                <div className="detail-card-icon date">
                                  <CalendarIcon size={16} />
                                </div>
                                <div className="detail-card-content">
                                  <span className="detail-label">Fecha confirmada</span>
                                  <span className="detail-value">
                                    {app.study_details?.confirmed_date || app.appointment_date || 'No especificada'}
                                  </span>
                                </div>
                              </div>

                              <div className="confirmed-detail-card">
                                <div className="detail-card-icon time">
                                  <Clock size={16} />
                                </div>
                                <div className="detail-card-content">
                                  <span className="detail-label">Hora confirmada</span>
                                  <span className="detail-value">
                                    {app.study_details?.confirmed_time ? `${app.study_details.confirmed_time} hs` : (app.appointment_time ? `${app.appointment_time.substring(0, 5)} hs` : 'No especificada')}
                                  </span>
                                </div>
                              </div>

                              <div className="confirmed-detail-card">
                                <div className="detail-card-icon address">
                                  <MapPin size={16} />
                                </div>
                                <div className="detail-card-content">
                                  <span className="detail-label">Dirección / Sede</span>
                                  <span className="detail-value">
                                    {app.study_details?.confirmed_address || app.study_details?.branch_name || 'No especificada'}
                                  </span>
                                </div>
                              </div>

                              <div className="confirmed-detail-card full-width">
                                <div className="detail-card-icon preparation">
                                  <AlertCircle size={16} />
                                </div>
                                <div className="detail-card-content">
                                  <span className="detail-label">Indicaciones / Preparación</span>
                                  <span className="detail-value preparation-text">
                                    {app.study_details?.confirmed_preparation || (app.study_details?.fasting ? 'Ayuno de 8 horas. Llevar orden médica.' : 'Sin indicaciones o preparación especial')}
                                  </span>
                                </div>
                              </div>

                              <div className="confirmed-detail-card full-width">
                                <div className="detail-card-icon details">
                                  <MessageSquare size={16} />
                                </div>
                                <div className="detail-card-content">
                                  <span className="detail-label">Aclaraciones / Detalles del Paciente</span>
                                  <span className="detail-value details-text">
                                    {app.study_details?.confirmed_details || app.patient_message || 'Sin comentarios adicionales'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ConfirmedAppointments;
